import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Container, Button } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ErrorResponse, GeneratedReportDto } from '@thingslog/repositories';
import { useToast } from '@thingslog/ui-components';
import Header from '../../components/header';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import PeriodPicker from '../../components/DatePicker/PeriodPicker';
import { ReduxState } from '../../reducers';
import { scheduledReportsQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import GeneratedScheduledReportsTable from './GeneratedScheduledReportsTable';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';

const GeneratedScheduledReports: FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get company ID and date range from Redux
  const selectedCompanyId = useDetermineCompanyId();
  const fromDateTz = useSelector((state: ReduxState) => state.period.fromDateTz);
  const toDateTz = useSelector((state: ReduxState) => state.period.toDateTz);

  const { useGeneratedReportsForCompanyBetweenDates } = useMemo(
    () => scheduledReportsQueryClient,
    []
  );

  const { data, isLoading } = useGeneratedReportsForCompanyBetweenDates(
    selectedCompanyId || 0,
    fromDateTz,
    toDateTz,
    {
      enabled: selectedCompanyId !== null,
      onError: (error: AxiosError<ErrorResponse>) => {
        const errorMessage = error.response?.data.message || t('generated_reports_default_error');

        toast({
          type: 'error',
          message: errorMessage,
          duration: 5000,
        });
      },
    }
  );

  if (selectedCompanyId === null) {
    return (
      <Header>
        <Container>
          <div className="text-2xl m-auto mt-20 text-center">
            {t('generated_reports_select_company_message')}
          </div>
        </Container>
      </Header>
    );
  }

  return (
    <Header>
      <Container>
        <section className="flex justify-center mb-6">
          <TitleHeader title={t('generated_reports_header')} />
        </section>
        <section className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
            <PeriodPicker alwaysShowDatePickers />
          </div>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SettingsIcon />}
            onClick={(): void => navigate('/app/ScheduledReportsConfig')}
          >
            {t('generated_reports_config_button')}
          </Button>
        </section>
        <section>
          {isLoading ? (
            <div className="flex justify-center p-8">
              <CircularProgress size={80} />
            </div>
          ) : (
            <GeneratedScheduledReportsTable
              generatedReportRows={
                data
                  ? data.map((report: GeneratedReportDto, index: number) => ({
                      ...report,
                      rowId: index,
                    }))
                  : []
              }
            />
          )}
        </section>
      </Container>
    </Header>
  );
};

export default GeneratedScheduledReports;
